import React from 'react';
import { DataTable } from 'Components/DataTable';
import { Column } from 'react-table';
import { useGetUserBrandEngagementQuery } from 'services';
import { useMediaQuery, theme, Text, VStack } from '@chakra-ui/react';
import { formatDateShort } from 'Utils/dataTableUtils';
import { IUserProductActivity } from 'Types/Interface/IUserProductActivity';
import { useLocale } from 'context/LocalizationContext';

export interface IBrandEngagementActivitiesTableProps {
	brandId: string;
}

export const BrandEngagementActivities = ({ brandId }: IBrandEngagementActivitiesTableProps) => {
	const { locale } = useLocale();
	const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
	const params = { brandId, locale };
	const { data, isLoading, isFetching, isError } = useGetUserBrandEngagementQuery(params);

	const newData = data?.data ? [{
		...data.data,
		id: data.data.productId || 'brand-engagement',
	}] : [];

	// Check if there are no interactions
	const hasNoInteractions = !!data?.data && [data.data.surveysCount, data.data.scansCount].every(count => !count);

	const columns: Column<IUserProductActivity>[] = React.useMemo(
		() => [
			{
				Header: 'First interaction',
				accessor: 'firstInteractionDate',
				Cell: (item) => <>{formatDateShort(item.row.original.firstInteractionDate)}</>,
			},
			{
				Header: 'Last interaction',
				accessor: 'lastInteractionDate',
				Cell: (item) => <>{formatDateShort(item.row.original.lastInteractionDate)}</>,
			},
			{
				Header: 'Total scans',
				accessor: 'scansCount',
				Cell: (item) => <>{item.row.original.scansCount || '—'}</>,
			},
			{
				Header: 'Total surveys',
				accessor: 'surveysCount',
				Cell: (item) => <>{item.row.original.surveysCount || '—'}</>,
			},
			{
				Header: 'Total earned',
				accessor: 'totalEarned',
				Cell: (item) => <>{item.row.original.totalEarned ? `${item.row.original.totalEarned} LNT` : '—'}</>,
			},
		],
		[],
	);

	const mobileColumns: Column<IUserProductActivity>[] = React.useMemo(
		() => [
			{
				accessor: 'firstInteractionDate',
				Cell: (item) => {
					const rowData = item.row.original;

					return (
						<VStack alignItems={'flex-start'} fontSize={'10px'} spacing={'0'} flex={1}>
							<Text fontSize={'12px'} fontWeight={500}>
								First interaction: {formatDateShort(rowData.firstInteractionDate)}
							</Text>
							<Text>Last interaction: {formatDateShort(rowData.lastInteractionDate)}</Text>
							<Text>Total scans: {rowData.scansCount || '—'}</Text>
							<Text>Total surveys: {rowData.surveysCount || '—'}</Text>
						</VStack>
					);
				},
			},
			{
				accessor: 'totalEarned',
				Cell: (item) => (
					<VStack alignItems={'flex-end'} spacing={'0'} flex={1} fontSize={'10px'}>
						<Text fontSize={'12px'} fontWeight={600}>
							{item.row.original.totalEarned ? `${item.row.original.totalEarned} LNT` : '—'}
						</Text>
					</VStack>
				),
			},
		],
		[],
	);

	// Show "No interactions yet" message if there are no interactions
	if (hasNoInteractions) {
		return (
			<VStack 
				alignItems="center" 
				justifyContent="center" 
				padding={6}
				spacing={2}
			>
				<Text 
					color="rgba(32, 32, 32, 0.7)" 
					fontSize="16px"
					textAlign="center"
				>
					No interactions yet
				</Text>
			</VStack>
		);
	}

	return (
		<DataTable
			data={newData}
			columns={isMobile ? mobileColumns : columns}
			pageCount={undefined}
			initialPageIndex={0}
			isLoading={isLoading || isFetching}
			isError={isError}
			pageSize={1}
			isMobile={isMobile}
			showCheckboxes={false}
			showMenu={false}
			cellPadding={3}
			isFlexible={true}
			tableContainerStyles={{border: 'none'}}
			headerStyles={{color: 'rgba(32, 32, 32, 0.7)', fontSize: '14px'}}
			bodyStyles={{color: '#202020' }}
		/>
	);
}; 